/**
* External dependencies
*/
import { isEmpty, findIndex } from 'lodash';
import classnames from 'classnames';

/**
* WordPress dependencies
*/
const { Component } = wp.element;
const { compose, withInstanceId } = wp.compose;
const { BaseControl, Button, Dashicon, ButtonGroup, RangeControl } = wp.components;

/**
* Internal dependencies
*/
import './editor.scss';

export const CSS_UNITS = [
	{ value: 'px', label: 'px', default: 0 },
	// { value: '%', label: '%', default: 10 },
	// { value: 'em', label: 'em', default: 0 },
	// { value: 'rem', label: 'rem', default: 0 },
	// { value: 'vw', label: 'vw', default: 10 },
	{ value: 'vh', label: '%', default: 10 },
];

export const DEFAULT_UNIT = CSS_UNITS[ 0 ];

class UnitButton extends Component {
	constructor( ) {
		super( ...arguments );

		this.onChangeUnit = this.onChangeUnit.bind( this );
		this.onChangeNumber = this.onChangeNumber.bind( this );
		this.resetCurrentInput = this.resetCurrentInput.bind( this );
		this.transformNumber = this.transformNumber.bind( this );

		this.hasUnits = this.hasUnits.bind( this );
		this.parseUnit = this.parseUnit.bind( this );

		this.state = {
			number: 0,
			unit: '',
			min: 0,
			max: 100,
			tmpInput: null,
		};
	}

	hasUnits( units ) {
		return ! isEmpty( units ) && units.length > 1 && units !== false;
	}

	/**
	 * Parses a number and unit from a value.
	 *
	 * @param {string} initialValue Value to parse
	 * @param {Array<Object>} units Units to derive from.
	 * @return {Array<number, string>} The extracted number and unit.
	 */
	parseUnit( initialValue, units = CSS_UNITS ) {
		const value = String( initialValue ).trim();

		let num = parseFloat( value, 10 );
		num = isNaN( num ) ? '' : num;

		const unitMatch = value.match( /[\d.\-\+]*\s*(.*)/ )[ 1 ];

		let unit = unitMatch !== undefined ? unitMatch : '';
		unit = unit.toLowerCase();

		if ( this.hasUnits( units ) ) {
			const match = units.find( ( item ) => item.value === unit );
			unit = match && match.value;
		} else {
			unit = DEFAULT_UNIT.value;
		}

		let baseValue = num;

		if ( isNaN( num ) || num === '' ) {
			baseValue = value;
		}

		const baseUnit = unit || units[ 0 ].value;

		return [ baseValue, baseUnit ];
	}

	componentDidMount( ) {
		const { value, units } = this.props;

		// const splitString = this.props.value.match( /(\d*\.?\d*)(.*)/ );
		// const number = splitString[ 1 ];
		// const unit = splitString[ 2 ];

		const [ number, unit ] = this.parseUnit( value, units );

		const currentIndex = findIndex( units, { value: unit } );

		this.setState( {
			number,
			unit,
			min: units[ currentIndex ].min,
			max: units[ currentIndex ].max,
		} );
	}

	componentDidUpdate( prevProps ) {
		if ( prevProps.value !== this.props.value ) {
			const [ number, unit ] = this.parseUnit( this.props.value, this.props.units );

			this.setState( {
				number,
				unit,
			} );
		}
	}

	resetCurrentInput() {
		if ( this.state.tmpInput !== null ) {
			this.setState( {
				tmpInput: null,
			} );
		}
	}

	transformNumber( fromUnit, toUnit ) {
		const { units } = this.props;

		const range = Math.abs( this.state.max - this.state.min );
		const percentPosition = ( this.state.number - this.state.min ) / range;

		const newUnitIndex = findIndex( units, { value: toUnit } );
		const newRange = Math.abs( units[ newUnitIndex ].max - units[ newUnitIndex ].min );

		const transformedNumber = ( percentPosition * newRange ) + units[ newUnitIndex ].min;

		return transformedNumber;
	}

	onChangeNumber( number ) {
		const { onChange } = this.props;
		// const newValue = event.target.value;

		this.setState( {
			number,
		}, () => {
			onChange( `${ this.state.number }${ this.state.unit }` );
		} );
	}

	onChangeUnit( event ) {
		const { units, onChange } = this.props;

		const newUnit = event.currentTarget.value;
		const newNumber = this.transformNumber( this.state.unit, newUnit );
		const newIndex = findIndex( units, { value: newUnit } );

		this.setState( {
			unit: newUnit,
			number: newNumber,
			min: units[ newIndex ].min,
			max: units[ newIndex ].max,
		}, () => {
			onChange( `${ this.state.number }${ this.state.unit }` );
		} );
	}

	render() {
		const {
			className,
			label,
			help,
			beforeIcon,
			id,
			units,
		} = this.props;

		return (
			<BaseControl
				label={ label }
				id={ id }
				help={ help }
				className={ classnames( 'eedee-components-unit-button', className ) }
			>
				{ beforeIcon && <Dashicon icon={ beforeIcon } /> }
				<div className="eedee-components-unit-button__wrap">
					<RangeControl
						className="eedee-components-unit-button__slider"
						id={ id }
						value={ this.state.number }
						onChange={ n => this.onChangeNumber( n ) }
						min={ this.state.min }
						max={ this.state.max }
					/>
					<ButtonGroup>
						{ units.map( ( unit, idx ) => {
							const { value, label } = unit;
							const isPrimary = value === this.state.unit;
							return (
								<Button
									className="eedee-components-unit-button__unit"
									key={ value }
									isPrimary={ isPrimary }
									isSecondary={ ! isPrimary }
									value={ value }
									onClick={ this.onChangeUnit }
								>
									{ label }
								</Button>
							);
						} ) }
					</ButtonGroup>
				</div>
			</BaseControl>
		);
	}
}

export default compose( [
	withInstanceId,
] )( UnitButton );
